// The site header — mobile nav toggle and the signed-in user menu. Rendered on
// every page by shared/_site_header, so it registers as a singleton.
import { registerSingleton } from "./first"

export const SiteHeader = {
  onDomReady(): void {
    mountSiteHeader()
  },
}

registerSingleton("SiteHeader", SiteHeader)

function mountSiteHeader(): void {
  document.querySelectorAll<HTMLElement>("[data-site-header]").forEach((header) => {
    const navToggle = header.querySelector<HTMLButtonElement>("[data-site-nav-toggle]")
    const navMenu = header.querySelector<HTMLElement>("[data-site-nav-menu]")

    if (navToggle && navMenu) {
      mountToggleMenu({
        root: header,
        toggle: navToggle,
        menu: navMenu,
        isOpen: () => navMenu.classList.contains("is-open"),
        setOpen: (open) => {
          navMenu.classList.toggle("is-open", open)
          navToggle.setAttribute("aria-expanded", open ? "true" : "false")
        },
      })
    }

    mountTouchNavGroups(header)

    header.querySelectorAll<HTMLElement>("[data-user-menu]").forEach((menuRoot) => {
      const toggle = menuRoot.querySelector<HTMLButtonElement>("[data-user-menu-toggle]")
      const menu = menuRoot.querySelector<HTMLElement>("[data-user-menu-dropdown]")
      if (!toggle || !menu) return

      mountToggleMenu({
        root: menuRoot,
        toggle,
        menu,
        isOpen: () => !menu.hidden,
        setOpen: (open) => {
          menu.hidden = !open
          toggle.setAttribute("aria-expanded", open ? "true" : "false")
        },
      })
    })
  })
}

// The nav dropdowns open on hover, which never fires on a touch screen. Below
// $bp-md the drawer flattens them out so it doesn't matter, but a tablet in
// landscape gets the hover layout with no way to reach it: the first tap on
// "Research" would just navigate. So on coarse pointers the first tap opens the
// group instead, and a second tap on the same label follows the link.
function mountTouchNavGroups(header: HTMLElement): void {
  if (!window.matchMedia("(hover: none), (pointer: coarse)").matches) return

  const groups = header.querySelectorAll<HTMLElement>("[data-nav-group]")

  const closeAll = (except?: HTMLElement) => {
    groups.forEach((group) => {
      if (group !== except) group.classList.remove("is-open")
    })
  }

  groups.forEach((group) => {
    const top = group.querySelector<HTMLAnchorElement>("[data-nav-group-top]")
    if (!top) return

    top.addEventListener("click", (event) => {
      // The drawer already shows every link, so let taps navigate there.
      if (getComputedStyle(group).flexDirection === "column") return
      if (group.classList.contains("is-open")) return

      event.preventDefault()
      closeAll(group)
      group.classList.add("is-open")
    })
  })

  document.addEventListener("click", (event) => {
    if (event.target instanceof Node && !header.contains(event.target)) closeAll()
  })
}

type ToggleMenu = {
  root: HTMLElement
  toggle: HTMLButtonElement
  menu: HTMLElement
  isOpen: () => boolean
  setOpen: (open: boolean) => void
}

function mountToggleMenu({ root, toggle, isOpen, setOpen }: ToggleMenu): void {
  toggle.addEventListener("click", (event) => {
    event.preventDefault()
    setOpen(!isOpen())
  })

  document.addEventListener("click", (event) => {
    if (!isOpen()) return
    if (event.target instanceof Node && !root.contains(event.target)) {
      setOpen(false)
    }
  })

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || !isOpen()) return
    setOpen(false)
    toggle.focus()
  })
}
